import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GTMEventsCrash } from '../enums/Crashlytics';
import { CrashlyticsService } from './crashlytics/crashlytics.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkService implements OnDestroy {

  private toastNetworkPresent: HTMLIonToastElement | null = null;
  private messageNet: { [key: string]: string } = {};

  constructor(
    private http: HttpClient
    , private toastController: ToastController
    , private crashlytics: CrashlyticsService
    , private translate: TranslateService
  ) {
    this.messageNet = this.translate.instant('MessageNetwork');
    this.preloadToast(this.messageNet.notConnection, this.messageNet.icon);
    this.initializeNetworkStatus();
  }

  ngOnDestroy() {
    if (Capacitor.isNativePlatform()) Network.removeAllListeners();
    else {
      window.removeEventListener('online', () => this.callbackConnect(true));
      window.removeEventListener('offline', () => this.callbackConnect(false));
    }
  }

  private async initializeNetworkStatus() {
    let status = false;
    if (Capacitor.isNativePlatform()) {
      status = (await Network.getStatus()).connected;
    } else {
      status = navigator.onLine;
    }
    this.callbackConnect(status);
  }

  public initializeNetworkEvents = () => {
    if (Capacitor.isNativePlatform()) {
      Network.addListener('networkStatusChange', (status) => {
        this.callbackConnect(status.connected);
      });
    } else {
      window.addEventListener('online', () => this.callbackConnect(true));
      window.addEventListener('offline', () => this.callbackConnect(false));
    }
  }

  private callbackConnect = async (connetion: boolean) => {
    await this.toastNetworkDismiss();
    if (connetion) {
      // Primero intentamos con la solicitud HTTP
      const resp = await this.validateRequestResponse();
      if (!resp) {
        // Si la solicitud HTTP falla, verificamos usando solo el API de Capacitor
        const capacitorCheck = await this.validateConnectionWithoutRequest();
        if (capacitorCheck) {
          // Mostramos el mensaje de red inestable, ya que hay conexión pero no funciona bien
          await this.toastNetwork(this.messageNet.unstableNet, undefined, this.messageNet.icon);
        } else {
          // Si ambos métodos indican que no hay conexión, mostramos el mensaje de sin conexión
          await this.toastNetwork(this.messageNet.notConnection, undefined, this.messageNet.icon);
        }
      } else {
        await this.toastNetworkDismiss();
      }
    } else {
      // El API de Capacitor ya indicó que no hay conexión
      await this.toastNetwork(this.messageNet.notConnection, undefined, this.messageNet.icon);
      await this.crashlytics.sendCrashPlatform(
        GTMEventsCrash.networkPlugin
        , { name: 'Without connection', message: 'You are not connected to the internet, check your connection' }
      );
    }
  }

  private toastNetwork = async (mensaje: string, title?: string, iconStart?: string) => {
    this.toastNetworkPresent = await this.toastController.create({
      header: title,
      message: mensaje,
      color: 'status-icon-enable',
      cssClass: 'toast-network',
      duration: 0,
      mode: 'ios',
      position: 'bottom',
      animated: true,
      keyboardClose: false,
      translucent: false,
      buttons: [
        {
          side: 'start',
          icon: iconStart || '',
          handler: () => false,
        },
        {
          icon: 'close',
          role: 'cancel',
          handler: () => { }
        }
      ],
    });

    await this.toastNetworkPresent.present();
  }

  /**
   * Método para crear un toast oculto, como precarga del controller para que muestre el toast al no tener conexión
   * @param message Mensaje del toast
   * @param iconStart Ícono del toast
   */
  private preloadToast = async (message: string, iconStart?: string) => {
    this.toastNetworkPresent = await this.toastController.create({
      message,
      duration: 1,
      cssClass: 'hidden-toast',
      buttons: [
        {
          side: 'start',
          icon: iconStart || '',
          handler: () => false,
        },
        {
          icon: 'close',
          role: 'cancel',
          handler: () => { }
        }
      ],
    });
    await this.toastNetworkPresent.present();
    await this.toastNetworkPresent.dismiss();
  }

  private toastNetworkDismiss = async () => {
    if (this.toastNetworkPresent) {
      await this.toastNetworkPresent.dismiss();
    }
  }

  private validateRequestResponse = () => {
    const apiAddress = 'https://www.cloudflare.com/cdn-cgi/trace'; // ** TODO Migracion: Dejar URL Configurable si es posible
    return firstValueFrom(this.http.get(apiAddress, { responseType: 'text' })
      .pipe(
        map(response => true),
        catchError(error => of(false))
      ));
  }

  /**
   * Método alternativo que verifica la conectividad usando solo el API de Capacitor
   * sin realizar solicitudes HTTP
   */
  private validateConnectionWithoutRequest = async (): Promise<boolean> => {
    try {
      if (Capacitor.isNativePlatform()) {
        const status = await Network.getStatus();
        return status.connected && (status.connectionType !== 'none');
      }
      return navigator.onLine;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtiene el estado actual de la red con información adicional
   * @returns Objeto con información detallada sobre la conexión
   */
  public getCurrentNetworkStatus = async () => {
    try {
      const status = await Network.getStatus();

      // Realizar una verificación HTTP si Capacitor indica que hay conexión
      let httpConnectivity = false;
      if (status.connected) {
        httpConnectivity = await this.validateRequestResponse();
      }

      return status.connected;
    } catch (error) {
      return false;
    }
  }
}
